<template>
  <div>
    <div v-if="isLoadingSummary" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="sr-only">Loading...</span>
      </div>
      <p class="mt-2 text-muted">Loading summary data...</p>
    </div>

    <div v-if="!isLoadingSummary" class="certificate-review-summary-wrapper mb-4">
      <div class="certificate-review-overview-card">
        <div class="certificate-review-summary-header">
          <div>
            <span class="summary-eyebrow">This Year</span>
            <h5 class="mb-0">External Certificate Review Summary</h5>
          </div>
          <div class="summary-rate">
            <strong>{{ currentReviewPercentage }}%</strong>
            <span>approval progress</span>
          </div>
        </div>

        <div class="progress mb-3" style="height: 18px">
          <div
            class="progress-bar bg-primary"
            role="progressbar"
            :style="`width: ${currentReviewPercentage}%;`"
            :aria-valuenow="currentReviewPercentage"
            aria-valuemin="0"
            aria-valuemax="100"
          >
            {{ currentReviewPercentage }}%
          </div>
        </div>

        <div class="summary-card-grid">
          <div class="status-card bg-warning">
            <div class="d-flex align-items-center justify-content-center mb-2">
              <div class="status-icon">
                <i class="fe fe-file-plus"></i>
              </div>
            </div>
            <div class="text-center text-white">
              <p class="mb-1">Certificate Submitted</p>
              <h3 class="mb-0">{{ currentSummary.submitted }}</h3>
            </div>
          </div>

          <div class="status-card bg-danger">
            <div class="d-flex align-items-center justify-content-center mb-2">
              <div class="status-icon">
                <i class="fe fe-clock"></i>
              </div>
            </div>
            <div class="text-center text-white">
              <p class="mb-1">In Review</p>
              <h3 class="mb-0">{{ currentSummary.in_review }}</h3>
            </div>
          </div>

          <div class="status-card bg-review-blue">
            <div class="d-flex align-items-center justify-content-center mb-2">
              <div class="status-icon">
                <i class="fe fe-edit-3"></i>
              </div>
            </div>
            <div class="text-center text-white">
              <p class="mb-1">Revision Note</p>
              <h3 class="mb-0">{{ currentSummary.revision }}</h3>
            </div>
          </div>

          <div class="status-card bg-success">
            <div class="d-flex align-items-center justify-content-center mb-2">
              <div class="status-icon">
                <i class="fe fe-check"></i>
              </div>
            </div>
            <div class="text-center text-white">
              <p class="mb-1">Approved</p>
              <h3 class="mb-0">{{ currentSummary.completed }}</h3>
            </div>
          </div>
        </div>
      </div>

      <div class="history-card">
        <span class="summary-eyebrow">Last Year</span>
        <h5 class="mb-3">Certificate Review</h5>
        <div class="history-list">
          <div>
            <span>Submitted</span>
            <strong>{{ previousSummary.submitted }}</strong>
          </div>
          <div>
            <span>In Review</span>
            <strong>{{ previousSummary.in_review }}</strong>
          </div>
          <div>
            <span>Approved</span>
            <strong>{{ previousSummary.completed }}</strong>
          </div>
        </div>
        <div class="history-progress">
          <div :style="`width: ${previousReviewPercentage}%;`"></div>
        </div>
        <small>{{ previousReviewPercentage }}% approval progress</small>
      </div>

      <div class="certificate-review-roadmap-card">
        <div class="certificate-review-summary-header">
          <div>
            <span class="summary-eyebrow">Roadmap</span>
            <h5 class="mb-0">Proses External Certificate Review</h5>
          </div>
        </div>

        <div class="certificate-review-roadmap">
          <div v-for="step in reviewSteps" :key="step.label" class="roadmap-step">
            <div class="roadmap-marker">
              <i :class="step.icon"></i>
            </div>
            <span>{{ step.label }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "Summary",
  props: {
    isLoadingSummary: {
      type: Boolean,
      default: false,
    },
    summaryData: {
      type: Object,
      default: () => ({
        previous: { enrolled: 0, in_review: 0, completed: 0, percentage: 0 },
        current: { enrolled: 0, in_review: 0, completed: 0, percentage: 0 },
      }),
    },
    approvalReport: {
      type: Object,
      default: () => ({
        approved: 0,
        revision: 0,
        pending: 0,
      }),
    },
  },
  computed: {
    currentSummary() {
      return this.normalizeCurrentSummary(this.approvalReport);
    },
    previousSummary() {
      return this.normalizeHistoricalSummary(this.summaryData?.previous);
    },
    currentReviewPercentage() {
      return this.calculateReviewPercentage(this.currentSummary);
    },
    previousReviewPercentage() {
      return this.calculateReviewPercentage(this.previousSummary);
    },
    reviewSteps() {
      return [
        { label: "Certificate Submitted", icon: "fe fe-file-plus" },
        { label: "Certificate Review", icon: "fe fe-file-text" },
        { label: "Revision Note", icon: "fe fe-edit-3" },
        { label: "Approved", icon: "fe fe-check" },
      ];
    },
  },
  methods: {
    normalizeCurrentSummary(report = {}) {
      const inReview = Number(report?.pending || 0);
      const revision = Number(report?.revision || 0);
      const completed = Number(report?.approved || 0);

      return {
        submitted: inReview + revision + completed,
        in_review: inReview,
        revision,
        completed,
      };
    },
    normalizeHistoricalSummary(summary = {}) {
      const enrolled = Number(summary?.enrolled ?? summary?.approved ?? 0);
      const inReview = Number(summary?.in_review || 0);
      const completed = Number(summary?.completed || 0);

      return {
        submitted: enrolled + inReview + completed,
        in_review: inReview,
        revision: Number(summary?.revision || 0),
        completed,
      };
    },
    calculateReviewPercentage(summary) {
      if (!summary.submitted) return 0;

      return Math.min(Math.round((summary.completed / summary.submitted) * 100), 100);
    },
  },
};
</script>

<style>
.certificate-review-summary-wrapper {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 260px;
  gap: 16px;
}

.certificate-review-overview-card,
.history-card,
.certificate-review-roadmap-card {
  border: 1px solid #e6edf7;
  border-radius: 8px;
  padding: 16px;
  background: #fff;
  box-shadow: 0 8px 24px rgba(31, 45, 61, 0.04);
}

.certificate-review-roadmap-card {
  grid-column: 1 / -1;
}

.certificate-review-summary-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
}

.summary-eyebrow {
  display: block;
  color: #90a4c4;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: uppercase;
  margin-bottom: 4px;
}

.summary-rate {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  color: #5e6b7f;
}

.summary-rate strong {
  color: #b80000;
  font-size: 22px;
  line-height: 1;
}

.summary-rate span {
  font-size: 12px;
  font-weight: 700;
}

.certificate-review-summary-wrapper .summary-card-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  overflow-x: auto;
  padding-bottom: 2px;
}

.history-list {
  display: grid;
  gap: 10px;
  margin-bottom: 16px;
}

.history-list div {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #edf1f6;
  padding-bottom: 8px;
}

.history-list span,
.history-card small {
  color: #5e6b7f;
  font-size: 12px;
  font-weight: 700;
}

.history-list strong {
  color: #14213d;
  font-size: 22px;
  line-height: 1;
}

.history-progress {
  height: 8px;
  border-radius: 8px;
  background: #edf1f6;
  overflow: hidden;
  margin-bottom: 8px;
}

.history-progress div {
  height: 100%;
  border-radius: inherit;
  background: #b80000;
}

.certificate-review-roadmap {
  position: relative;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 10px;
  padding-top: 6px;
}

.certificate-review-roadmap::before {
  content: "";
  position: absolute;
  top: 21px;
  left: 14px;
  right: 14px;
  height: 2px;
  background: #dbe4f0;
}

.roadmap-step {
  position: relative;
  z-index: 1;
  min-width: 0;
}

.roadmap-marker {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 2px solid #08ce79;
  background: #eafff5;
  color: #08a867;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 6px;
  font-size: 12px;
}

.roadmap-step span {
  display: block;
  color: #14213d;
  font-size: 11px;
  font-weight: 700;
  line-height: 1.25;
}

.certificate-review-summary-wrapper .status-card {
  border-radius: 8px;
  padding: 16px;
  min-height: 112px;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.certificate-review-summary-wrapper .status-icon {
  width: 44px;
  height: 44px;
  background-color: rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  color: white;
}

.certificate-review-summary-wrapper .status-card p {
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 0;
}

.certificate-review-summary-wrapper .status-card h3 {
  font-size: 30px;
  font-weight: bold;
  margin: 0;
}

.bg-review-blue {
  background-color: #4f8edc;
}

.progress {
  background-color: #e9ecef;
  border-radius: 0.5rem;
  overflow: hidden;
}

.progress-bar {
  font-weight: 600;
  font-size: 14px;
}

@media (max-width: 991.98px) {
  .certificate-review-summary-wrapper,
  .certificate-review-roadmap {
    grid-template-columns: 1fr;
  }

  .certificate-review-roadmap::before {
    display: none;
  }

  .summary-rate {
    align-items: flex-start;
  }
}

@media (max-width: 575.98px) {
  .certificate-review-summary-header {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>
